import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { copyTextToClipboard, shareURL } from '@tma.js/sdk-react';
import { Film, Share2 } from 'lucide-react';

import { env } from '@/env';
import { useHaptics } from '@/hooks/useHaptics';
import { useQcpMe } from '@/hooks/useQcpMe';
import { useGetMatchQuery, type MatchSummary } from '@/services/qchinesepokerApi';
import { postoMio } from '@/lib/qcp/rest';
import { chiHaAbbandonato } from '@/lib/qcp/resa';
import type { MatchResultPayload, Seat } from '@/lib/qcp/protocol';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

/**
 * Router state shape passed by ChinesePokerMatch when the multi-hand set ends.
 *
 * The match-over trigger is now `match:result` (MatchResultPayload) — the
 * canonical end of the multi-hand "set" — NOT the legacy single-hand
 * `match:ended` (EndedPayload). The result screen reads `matchResult` from
 * router location state and falls back to the RTK MatchSummary on a
 * refresh / deep-link.
 */
interface ResultState {
  matchResult: MatchResultPayload;
  mySeat: Seat;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** The per-seat final cumulative net for `seat`, taken from the match result. */
function finalNetFor(result: Pick<MatchResultPayload, 'finalNetA' | 'finalNetB'>, seat: Seat): number {
  return seat === 'A' ? result.finalNetA : result.finalNetB;
}

/**
 * Map the server `reason` enum to a locale key. `resign`, `forfeit` and `abandoned` share a NEUTRAL label (T2b): the text does not
 * say WHO left, because we do not know who the player is until `chinesepoker.me.read` exists (L-3104, I-646) — «Avversario
 * abbandonato» was false whenever the player was the one who resigned.
 */
function reasonKey(
  reason: string,
):
  | 'result.reasonComplete'
  | 'result.reasonDraw'
  | 'result.reasonAbandoned' {
  switch (reason) {
    case 'draw':
      return 'result.reasonDraw';
    case 'resign':
    case 'forfeit':
    case 'abandoned':
      return 'result.reasonAbandoned';
    case 'complete':
    default:
      return 'result.reasonComplete';
  }
}

/** Derive outcome key + colour from the match result relative to mySeat. */
function deriveOutcome(
  result: Pick<MatchResultPayload, 'winner' | 'finalNetA' | 'finalNetB'>,
  mySeat: Seat,
): { labelKey: 'result.youWon' | 'result.youLost' | 'result.tie'; netLabel: string; badgeClass: string } {
  const net = finalNetFor(result, mySeat);
  const netLabel = net > 0 ? `+${net}` : `${net}`;

  if (result.winner === null) {
    return {
      labelKey: 'result.tie',
      netLabel,
      badgeClass: 'bg-amber-500/20 text-amber-300',
    };
  }
  if (result.winner === mySeat) {
    return {
      labelKey: 'result.youWon',
      netLabel,
      badgeClass: 'bg-emerald-500/20 text-emerald-300',
    };
  }
  return {
    labelKey: 'result.youLost',
    netLabel,
    badgeClass: 'bg-rose-500/20 text-rose-300',
  };
}

/**
 * L'intestazione di una partita letta dal BFF (ricarica o link diretto: niente stato del router). Con `me` noto (X5 di tma-orcb) e' dal MIO
 * posto, come l'intestazione col stato del router; senza (X6: nessun profilo, 424, caricamento, rete giu', forma rotta, id non in
 * partita) resta com'era: «Risultato partita», «A – B» e «Posto A – Posto B». Il hook sta qui e non nella schermata: con lo stato del
 * router (dal socket) `me` non serve e non si chiede.
 */
function SommarioDaServer({ summary }: { summary: MatchSummary }) {
  const { t } = useTranslation('chinesepoker');
  const posto = postoMio(summary.players, useQcpMe());
  const mio = posto
    ? deriveOutcome({ winner: summary.winnerSeat, finalNetA: summary.net.A, finalNetB: summary.net.B }, posto)
    : null;
  const resa = chiHaAbbandonato(summary.reason, summary.winnerSeat, posto);
  if (mio && resa) {
    return <span className={`rounded-full px-4 py-1 text-sm font-bold uppercase tracking-widest ${mio.badgeClass}`}>{t(resa === 'io' ? 'result.reasonResignMine' : 'result.reasonResignOpp')}</span>;
  }
  if (mio) {
    return (
      <>
        <span className={`rounded-full px-4 py-1 text-sm font-bold uppercase tracking-widest ${mio.badgeClass}`}>{t(mio.labelKey)}</span>
        <p className="text-4xl font-bold tabular-nums text-fg">{mio.netLabel}</p>
        <p className="text-xs font-medium text-fg-muted">{t('result.points')}</p>
        <p className="text-xs font-medium text-fg-muted">{t(reasonKey(summary.reason))}</p>
      </>
    );
  }
  return (
    <>
      <span className="rounded-full bg-bg-tertiary px-4 py-1 text-sm font-semibold text-fg-muted uppercase tracking-widest">
        {t('result.matchResult')}
      </span>
      <p className="text-4xl font-bold tabular-nums text-fg">
        {summary.net.A} – {summary.net.B}
      </p>
      <p className="text-xs font-medium text-fg-muted">{t('result.seatLabel')}</p>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/**
 * Post-match Chinese Poker result screen (multi-hand "set").
 *
 * Reached from ChinesePokerMatch when `match:result` fires — the match screen
 * navigates here with { matchResult, mySeat } in router location state. The
 * payload carries the final cumulative net (finalNetA / finalNetB), the set
 * winner, the number of hands played (incl. FL extensions), the end reason and
 * the per-seat ELO deltas (null when a bot is involved).
 *
 * Also tolerates a deep-link / page-refresh (state absent): falls back to
 * fetching the MatchSummary via RTK Query and shows a neutral header.
 *
 * Card image: SPENTA in T1b. `GET /matches/:id/card.png` (pubblica su QCP) non ha un equivalente nel BFF di Limulo e la TMA non
 * chiama piu' QCP direttamente: al suo posto lo stato «carta non disponibile» e il risultato in testo.
 *
 * Share: `shareURL` from @tma.js/sdk-react opens Telegram's native contact
 * share sheet. Guarded with `shareURL.isAvailable()` so it no-ops outside
 * Telegram (browser dev, refresh outside the WebView). Clipboard fallback
 * mirrors InviteSheet.
 */
export function ChinesePokerResult() {
  const { t } = useTranslation('chinesepoker');
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const haptics = useHaptics();

  // ── Router state (present when navigated from ChinesePokerMatch) ────
  const routerState = (location.state as ResultState | null) ?? null;
  const matchResult = routerState?.matchResult ?? null;
  const mySeat = routerState?.mySeat ?? null;

  // ── Fallback: fetch match summary when state is absent (refresh/deep-link)
  const { data: matchSummary } = useGetMatchQuery(id, {
    skip: Boolean(matchResult) || !id,
  });

  // ── Share state ─────────────────────────────────────────────────────
  const [copied, setCopied] = useState(false);

  // ── Derive outcome ──────────────────────────────────────────────────
  const outcome =
    matchResult && mySeat
      ? deriveOutcome(matchResult, mySeat)
      : null;
  // Una resa dice chi ha abbandonato e NON mostra un esito a punti (v. `chiHaAbbandonato`).
  const resa = matchResult ? chiHaAbbandonato(matchResult.reason, matchResult.winner, mySeat) : null;

  // ── ELO delta for this seat (null when a bot is involved) ────────────
  const myEloDelta =
    matchResult && mySeat && matchResult.elo
      ? matchResult.elo[mySeat].delta
      : null;

  // ── Share handler ──────────────────────────────────────────────────
  async function handleShare() {
    haptics.impact('light');
    const botLink = `https://t.me/${env.telegramBotUsername}`;

    // Build the share text using locale keys.
    let text: string;
    if (outcome && !resa) {
      text = t('result.shareText', {
        result: t(outcome.labelKey),
        net: outcome.netLabel,
        app: botLink,
      });
    } else {
      text = t('result.shareTextDefault', { app: botLink });
    }

    // shareURL opens Telegram's native share sheet — guarded with the SDK's
    // own isAvailable() signal so it no-ops outside Telegram (browser dev,
    // refresh outside the WebView).
    if (shareURL.isAvailable()) {
      try {
        shareURL(botLink, text);
      } catch {
        /* Not in Telegram — fall through to clipboard */
      }
    }

    // Clipboard fallback: mirrors InviteSheet.tsx's two-level try/catch.
    try {
      await copyTextToClipboard(text);
    } catch {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        /* clipboard unavailable — silently no-op */
      }
    }

    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_500);
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div
      className="mx-auto flex max-w-[520px] flex-col items-center gap-6 px-4 py-8"
      style={{ paddingTop: 'calc(var(--tg-viewport-safe-area-inset-top, 0px) + 2rem)' }}
    >
      {/* ── Result header ──────────────────────────────────────────── */}
      <header className="flex w-full flex-col items-center gap-2 rounded-2xl bg-bg-secondary px-6 py-5 text-center">
        {outcome && matchResult && resa ? (
          <span className={`rounded-full px-4 py-1 text-sm font-bold uppercase tracking-widest ${outcome.badgeClass}`}>
            {t(resa === 'io' ? 'result.reasonResignMine' : 'result.reasonResignOpp')}
          </span>
        ) : outcome && matchResult ? (
          <>
            <span
              className={`rounded-full px-4 py-1 text-sm font-bold uppercase tracking-widest ${outcome.badgeClass}`}
            >
              {t(outcome.labelKey)}
            </span>
            <p className="text-4xl font-bold tabular-nums text-fg">
              {outcome.netLabel}
            </p>
            <p className="text-xs font-medium text-fg-muted">{t('result.points')}</p>
            <p className="text-xs font-medium text-fg-muted">{t(reasonKey(matchResult.reason))}</p>
          </>
        ) : matchSummary ? (
          <SommarioDaServer summary={matchSummary} />
        ) : (
          <span className="text-sm font-semibold text-fg-muted">{t('result.matchResult')}</span>
        )}
      </header>

      {/* ── Match summary stats (final score / hands played / ELO) ───── */}
      {matchResult && mySeat && (
        <div className="grid w-full grid-cols-2 gap-3">
          {/* Final cumulative score (both seats, relative to me) */}
          <div className="col-span-2 flex items-center justify-between rounded-2xl bg-bg-secondary px-5 py-4">
            <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {t(resa ? 'result.scoreAccumulated' : 'result.finalScore')}
            </span>
            <span className="text-lg font-bold tabular-nums text-fg">
              {finalNetFor(matchResult, mySeat)} – {finalNetFor(matchResult, mySeat === 'A' ? 'B' : 'A')}
            </span>
          </div>

          {/* Hands played */}
          <div className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-bg-secondary px-4 py-4 text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {t('result.handsPlayed', { count: matchResult.handsPlayed })}
            </span>
          </div>

          {/* ELO delta — only when present (null with a bot) */}
          <div className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-bg-secondary px-4 py-4 text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {t('result.eloDelta')}
            </span>
            {myEloDelta !== null ? (
              <span
                className={`text-lg font-bold tabular-nums ${
                  myEloDelta > 0
                    ? 'text-emerald-300'
                    : myEloDelta < 0
                      ? 'text-rose-300'
                      : 'text-fg'
                }`}
              >
                {myEloDelta > 0 ? `+${myEloDelta}` : `${myEloDelta}`}
              </span>
            ) : (
              <span className="text-lg font-bold text-fg-muted">—</span>
            )}
          </div>
        </div>
      )}

      {/* ── Share card ───────────────────────────────────────────────── */}
      {/* T1b: la PNG (`GET /matches/:id/card.png`, pubblica su QCP) non ha un equivalente nel BFF di Limulo (le capability sono tutte
          JSON) e la TMA non chiama piu' QCP direttamente: la carta e' SPENTA con il suo stato, il testo che c'era come ripiego. */}
      <div className="w-full overflow-hidden rounded-2xl bg-bg-secondary">
        <div className="flex flex-col items-center gap-1 px-4 py-6 text-center">
          {outcome && !resa && (
            <p className="text-sm font-semibold text-fg">
              {t(outcome.labelKey)} {outcome.netLabel} {t('result.points')}
            </p>
          )}
          <p className="text-xs text-fg-muted">{t('result.cardUnavailable')}</p>
        </div>
      </div>

      {/* ── Actions ──────────────────────────────────────────────────── */}
      <div className="flex w-full flex-col gap-3">
        {/* Share button */}
        <button
          type="button"
          onClick={handleShare}
          className="flex items-center justify-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-brand-fg transition-opacity active:opacity-80"
        >
          <Share2 className="h-4 w-4" />
          {copied ? t('result.copied') : t('result.share')}
        </button>

        {/* Replay CTA */}
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            navigate(`/chinesepoker/replay/${id}`);
          }}
          className="flex items-center justify-center gap-2 rounded-xl border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-fg transition-opacity active:opacity-80"
        >
          <Film className="h-4 w-4" />
          {t('result.viewReplay')}
        </button>

        {/* Play again CTA */}
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            navigate('/chinesepoker');
          }}
          className="rounded-xl border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-fg transition-opacity active:opacity-80"
        >
          {t('result.playAgain')}
        </button>
      </div>
    </div>
  );
}
